import { reflect } from '@effector/reflect';
import { TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { PenSquare } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DEVICE_NAME_MIN_LENGTH, DEVICE_NAME_MAX_LENGTH } from 'entities/device';
import { $t, breakpoints, translated } from 'shared/lib';
import { createModalForm, ModalFormProps } from 'shared/ui';
import { EditDeviceModel } from '../../model';

export const createEditDeviceModal = (model: EditDeviceModel) => {
	const ModalForm = createModalForm(model);

	const NameField = reflect({
		view: TextInput,
		bind: {
			'data-test-id': 'edit-device-name-input',
			size: breakpoints.md.map((md) => (md ? '2xl' : 'xl')),
			label: translated('edit-device.name'),
			value: model.form.fields.name.$value,
			errorProps: { 'data-test-id': 'edit-device-name-input-error' },
			error: combine(
				[model.form.fields.name.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('edit-device.name'),
						minLength: DEVICE_NAME_MIN_LENGTH,
						maxLength: DEVICE_NAME_MAX_LENGTH,
					}),
			),
			onChange: (e) => model.form.fields.name.change(e.target.value),
		},
	});

	const EditDeviceModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [pending, md] = useUnit([model.$pending, breakpoints.md]);

		return (
			<ModalForm
				ref={ref}
				title={t('edit-device.title')}
				submitButtonProps={{
					children: t('edit-device.save'),
					leftSection: <PenSquare />,
					loading: pending,
					fullWidth: true,
					size: md ? 'xl' : 'lg',
				}}
				size={md ? '2xl' : 'xl'}
				{...props}
			>
				<NameField />
			</ModalForm>
		);
	});

	const EditDeviceModalActionIcon = reflect({
		view: ModalForm.ActionIcon,
		bind: {
			'data-test-id': 'edit-device-action-icon',
			variant: 'white',
			children: <PenSquare />,
			size: breakpoints.md.map((md) => (md ? 'xl' : 'lg')),
		},
	});

	return Object.assign(EditDeviceModal, {
		ActionIcon: EditDeviceModalActionIcon,
	});
};
